import { ArrowRight, Headphones, Square } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { player } from '../audio/player';
import { Avatar } from '../components/Cast';
import { Illustration } from '../components/Illustration';
import { StepHead } from '../components/StepHead';
import { getItem } from '../content';
import type { Lesson } from '../content/types';
import { clipFor, sceneVoice, speechRange } from '../lib/voice';

interface Props {
  lesson: Lesson;
  number: number;
  newCount: number;
  onDone: () => void;
}

/** The hook: what you'll be able to do, and the scene played through once to listen for the gist. */
export function LessonIntro({ lesson, number, newCount, onDone }: Props) {
  const [playing, setPlaying] = useState(-1);
  // Listen for the gist before reading: the English appears after the first play (or on request).
  const [heard, setHeard] = useState(false);
  const lines = lesson.scene.turns.filter((t) => t.who !== 'narrator') as Array<{ who: 'staff' | 'you'; item: string; slow?: boolean; surprise?: boolean }>;

  const run = useRef(0);
  useEffect(
    () => () => {
      run.current++;
      player.stop();
    },
    [],
  );

  const listen = async () => {
    if (playing >= 0) {
      run.current++;
      player.stop();
      setPlaying(-1);
      setHeard(true);
      return;
    }
    const mine = ++run.current;
    for (let i = 0; i < lines.length; i++) {
      if (run.current !== mine) return;
      setPlaying(i);
      const item = getItem(lines[i].item);
      const clip = clipFor(item, sceneVoice(item, lines[i].who, lesson.staff));
      const result = await player.play(clip.src, { ...speechRange(clip), rate: lines[i].slow ? 0.75 : 1, tag: 'intro' });
      if (result !== 'ended' || run.current !== mine) break;
      await new Promise((r) => setTimeout(r, 450));
    }
    if (run.current !== mine) return;
    setPlaying(-1);
    setHeard(true);
  };

  return (
    <div className="step">
      <Illustration id={lesson.id} size="scene" priority className="intro-art" />
      <div className="stack">
        <p className="eyebrow">Lesson {number}</p>
        <h2 className="title">{lesson.title}</h2>
        <p className="subtitle">
          You'll learn to <strong>{lesson.cando.charAt(0).toLowerCase() + lesson.cando.slice(1)}</strong>
        </p>
        <p className="muted">
          {newCount} new {newCount === 1 ? 'phrase' : 'phrases'} · about {Math.max(4, Math.round(newCount * 1.6 + 2))} minutes
        </p>
      </div>

      <div className="panel stack-lg">
        <div className="stack" style={{ gap: 4 }}>
          <StepHead kind="gist" />
          <p className="section-title">Listen to the scene first</p>
          <p className="muted small">Just the gist: the English shows after, and you learn each line next.</p>
        </div>
        <div className="row-wrap">
          <button type="button" className={`btn ${heard ? 'btn-secondary' : 'btn-primary'}`} onClick={listen}>
            {playing >= 0 ? <Square size={18} aria-hidden="true" /> : <Headphones size={18} aria-hidden="true" />}
            {playing >= 0 ? 'Stop' : heard ? 'Play it again' : 'Play the scene'}
          </button>
          {!heard && playing < 0 && (
            <button type="button" className="btn btn-ghost small" onClick={() => setHeard(true)}>
              Can't listen? Show the English
            </button>
          )}
        </div>
        <ol className="stack" style={{ listStyle: 'none', padding: 0, margin: 0, gap: 8 }}>
          {lines.map((line, i) => {
            const item = getItem(line.item);
            return (
              <li
                key={i}
                className="intro-line"
                style={{ opacity: playing < 0 || playing === i ? 1 : 0.45 }}
                aria-current={playing === i ? 'true' : undefined}
              >
                <Avatar
                  cast={line.who === 'staff' ? lesson.staff.cast : 'you'}
                  name={line.who === 'staff' ? lesson.staff.role : 'You'}
                  size={28}
                  active={playing === i}
                />
                <span className="bubble-who" style={{ minWidth: '3.6rem' }}>
                  {line.who === 'staff' ? lesson.staff.role : 'You'}
                </span>
                <span className={line.surprise || !heard ? 'muted' : undefined}>
                  {!heard ? '· · ·' : line.surprise ? 'Something you haven’t learned yet' : item.meaning}
                </span>
              </li>
            );
          })}
        </ol>
      </div>

      <div className="actions">
        <button
          type="button"
          className={`btn btn-block btn-lg ${heard ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => {
            run.current++;
            player.stop();
            onDone();
          }}
        >
          Start the lesson <ArrowRight size={20} aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}
